import { createContext, useContext, useEffect, useState, ReactNode } from "react";

// Telegram WebApp types
interface TelegramWebApp {
    initData: string;
    initDataUnsafe: {
        user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
            language_code?: string;
            is_premium?: boolean;
            photo_url?: string;
        };
        auth_date: number;
        hash: string;
        start_param?: string;  // Параметр из deep link (?start=ref_CODE)
        query_id?: string;
    };
    colorScheme: "light" | "dark";
    themeParams: {
        bg_color: string;
        text_color: string;
        hint_color: string;
        link_color: string;
        button_color: string;
        button_text_color: string;
    };
    isExpanded: boolean;
    viewportHeight: number;
    viewportStableHeight: number;
    headerColor: string;
    backgroundColor: string;
    platform: string;
    version: string;
    ready: () => void;
    expand: () => void;
    close: () => void;
    enableClosingConfirmation: () => void;
    disableClosingConfirmation: () => void;
    setHeaderColor: (color: string) => void;
    setBackgroundColor: (color: string) => void;
    showAlert: (message: string) => void;
    showConfirm: (message: string, callback: (confirmed: boolean) => void) => void;
    showPopup: (params: any, callback?: (id: string) => void) => void;
    MainButton: {
        text: string;
        color: string;
        textColor: string;
        isVisible: boolean;
        isActive: boolean;
        isProgressVisible: boolean;
        setText: (text: string) => void;
        onClick: (callback: () => void) => void;
        offClick: (callback: () => void) => void;
        show: () => void;
        hide: () => void;
        enable: () => void;
        disable: () => void;
        showProgress: (leaveActive?: boolean) => void;
        hideProgress: () => void;
    };
    BackButton: {
        isVisible: boolean;
        onClick: (callback: () => void) => void;
        offClick: (callback: () => void) => void;
        show: () => void;
        hide: () => void;
    };
}

declare global {
    interface Window {
        Telegram?: {
            WebApp: TelegramWebApp;
        };
    }
}

interface TelegramContextType {
    webApp: TelegramWebApp | null;
    user: TelegramWebApp["initDataUnsafe"]["user"] | null;
    startParam: string | null;  // Deep link параметр
    isReady: boolean;
}

const TelegramContext = createContext<TelegramContextType>({
    webApp: null,
    user: null,
    startParam: null,
    isReady: false,
});

export const useTelegram = () => useContext(TelegramContext);

interface TelegramProviderProps {
    children: ReactNode;
}

export const TelegramProvider = ({ children }: TelegramProviderProps) => {
    const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
    const [user, setUser] = useState<TelegramWebApp["initDataUnsafe"]["user"] | null>(null);
    const [startParam, setStartParam] = useState<string | null>(null);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        const tg = window.Telegram?.WebApp;

        if (tg) {
            tg.ready();
            tg.expand();
            
            // Настройка цветовой схемы под Telegram
            tg.setHeaderColor("#0e0e11");
            tg.setBackgroundColor("#0e0e11");
            
            setWebApp(tg);
            setUser(tg.initDataUnsafe.user || null);
            setStartParam(tg.initDataUnsafe.start_param || null);
            setIsReady(true);
        } else {
            setIsReady(true);
        }
    }, []);

    return (
        <TelegramContext.Provider value={{ webApp, user, startParam, isReady }}>
            {children}
        </TelegramContext.Provider>
    );
};

// Hook для использования Telegram-специфичных функций
export const useTelegramMainButton = (
    text: string,
    onClick: () => void,
    options?: {
        color?: string;
        textColor?: string;
        disabled?: boolean;
    }
) => {
    const { webApp } = useTelegram();

    useEffect(() => {
        if (!webApp?.MainButton) return;

        const button = webApp.MainButton;

        button.setText(text);
        if (options?.color) button.color = options.color;
        if (options?.textColor) button.textColor = options.textColor;
        
        if (options?.disabled) {
            button.disable();
        } else {
            button.enable();
        }

        button.onClick(onClick);
        button.show();

        return () => {
            button.offClick(onClick);
            button.hide();
        };
    }, [webApp, text, onClick, options?.color, options?.textColor, options?.disabled]);
};

// Hook для кнопки "Назад"
export const useTelegramBackButton = (onClick: () => void, show: boolean = true) => {
    const { webApp } = useTelegram();

    useEffect(() => {
        if (!webApp?.BackButton) return;

        const button = webApp.BackButton;

        button.onClick(onClick);
        
        if (show) {
            button.show();
        } else {
            button.hide();
        }

        return () => {
            button.offClick(onClick);
            button.hide();
        };
    }, [webApp, onClick, show]);
};
